import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { UserService } from '@duck/api-definitions'
import { apis } from '@/modules/apiClients'

export interface AdminUserQueryFilters {
  role?: UserService.UserRole[]
  isActive?: boolean
  isVerified?: boolean
  accessGroups?: string[]
  search?: string
  organizationId?: string[]
}

export interface UseAdminUsersParams {
  queryFilters: AdminUserQueryFilters
  sortField?: keyof UserService.User
  sortDirection?: 'asc' | 'desc'
  initialPageSize?: number
}

const DEFAULT_PAGE_SIZE = 30

export const adminUsersQueryKeys = {
  all: () => ['admin-users'] as const,
  list: (params: { queryFilters: AdminUserQueryFilters; sortField?: keyof UserService.User; sortDirection?: 'asc' | 'desc' }) =>
    ['admin-users', params] as const,
}

const fetchAdminUsersPage = async ({
  pageParam,
  queryFilters,
  sortField,
  sortDirection,
}: {
  pageParam: { limit: number; offset: number }
  queryFilters: AdminUserQueryFilters
  sortField?: keyof UserService.User
  sortDirection?: 'asc' | 'desc'
}): Promise<UserService.User[]> => {
  const { limit, offset } = pageParam

  const request: UserService.ListUsersRequest = {
    filter: {
      role: queryFilters.role?.length ? queryFilters.role : undefined,
      isActive: queryFilters.isActive,
      isVerified: queryFilters.isVerified,
      accessGroups: queryFilters.accessGroups?.length ? queryFilters.accessGroups : undefined,
      search: queryFilters.search?.trim() || undefined,
      organizationId: queryFilters.organizationId?.length ? queryFilters.organizationId : undefined,
    },
    sortField: sortField as UserService.UserSortFieldEnum | undefined,
    sortDirection: sortDirection as UserService.SortDirectionEnum | undefined,
    limit,
    offset,
  }

  const response = await apis.userService.listUsers(request)
  return response.data
}

export const useAdminUsers = ({
  queryFilters,
  sortField,
  sortDirection,
  initialPageSize = DEFAULT_PAGE_SIZE,
}: UseAdminUsersParams) => {
  const query = useInfiniteQuery({
    queryKey: adminUsersQueryKeys.list({ queryFilters, sortField, sortDirection }),
    queryFn: ({ pageParam }) =>
      fetchAdminUsersPage({
        pageParam: pageParam ?? { limit: initialPageSize, offset: 0 },
        queryFilters,
        sortField,
        sortDirection,
      }),
    initialPageParam: { limit: initialPageSize, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length === 0) return undefined
      const currentOffset = allPages.reduce((sum, page) => sum + page.length, 0)
      const nextLimit = allPages.length === 1
        ? initialPageSize * 2
        : Math.min(initialPageSize * 2 ** allPages.length, initialPageSize * 2 ** 8)
      return { limit: nextLimit, offset: currentOffset }
    },
  })

  const users = useMemo(() => query.data?.pages.flat() ?? [], [query.data])
  const hasMore = !!query.hasNextPage

  return {
    users,
    hasMore,
    isInitialLoading: query.isPending,
    isFetchingNextPage: query.isFetchingNextPage,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  }
}
